import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { FunnelStep, MeterDetail } from '@rheora/enrollment-contract';

import type { ConsumptionSelectionId } from '../services/consumption';
import type { FunnelSeed } from '../services/funnel-rehydrate.service';
import { furthestServerStep } from '../services/funnelGating';
import { DEFAULT_FUNNEL_METHOD, type FunnelMethod } from '../services/funnelMethod';

/**
 * The meter-first funnel's tiny in-memory store (§4.1 of the epic).
 *
 * ── WHY A CONTEXT, NOT TANSTACK QUERY ─────────────────────────────────────
 * TanStack Query is the server-state cache — but the funnel's cross-step
 * ephemeral facts (which method the shopper picked, the opaque candidateId
 * the BFF minted for them, the meter-detail card fields to redisplay) are
 * LOCAL UI/session state. The server's source of truth for the ESI ID stays
 * in the funnel session cookie; this context only holds the browser-side
 * display and the OPAQUE handle (AC3 — never the raw ESI ID, never any key
 * a later hop trusts).
 *
 * ── LIFETIME, AND THE PROMISE THIS FILE USED TO BREAK ─────────────────────
 * One instance mounted at the storefront layout root so it survives every
 * navigation inside the storefront (chooser → sub-flow → /plans → funnel).
 *
 * This header used to say a fresh reload resets everything, "that is
 * deliberate: the server session is what a returning tab redeems from, not
 * this browser memory". The first half was true and the second was aspiration:
 * THE REDEMPTION PATH DID NOT EXIST, so a refresh simply lost the shopper's
 * progress. It exists now — `GET /bff/funnel` — and `FunnelHydrationGate`
 * mounts this provider already SEEDED from it (plus the URL, for the usage
 * figure the server never held). A reload still resets this memory; it just no
 * longer resets the FUNNEL.
 *
 * ⚠️ Some things still do not come back, on purpose: the SSN (never persisted
 * anywhere — see CreditPage), the date of birth, the customer's name/phone,
 * and any in-flight OTP code.
 *
 * ── MINIMAL SURFACE ───────────────────────────────────────────────────────
 * `{ method, candidateId, meterDetail, zip }` plus setters. Anything else a
 * page needs (plan slug, credit approval, etc.) comes from URL params or
 * TanStack keyed to the candidateId. Keep this store dumb.
 */

/**
 * The full state object every consumer reads. `meterDetail` is the DISPLAY-ONLY
 * `MeterDetail` the ESI ID lookup resolved (from either on-ramp). Its `esiid`
 * field is a confirmation shown to the customer ("this is your meter") — never a
 * redemption key. The opaque `candidateId` (the handle the funnel trusts) is held
 * separately.
 *
 * Both on-ramps converge here: the ZIP path resolves an address → ESI ID →
 * `MeterDetail`; the ESI ID path types the ESI ID → `MeterDetail`. One shape, one
 * card, one dialog.
 */
export interface FunnelState {
  /**
   * The furthest step the SERVER can evidence, from `GET /bff/funnel` (E-SF19 P1).
   *
   * ⚠️ READ-ONLY AND DELIBERATELY WITHOUT A SETTER. Every other field here is
   * browser state the shopper edits; this one is the server's own answer about
   * what its session contains, and a setter would let a component advance it —
   * which is precisely the capability the gate exists to withhold.
   *
   * ⚠️ IT IS NOT FROZEN, THOUGH. It tracks the FURTHEST step any settled snapshot
   * has evidenced, because the shopper advances the server without this provider
   * unmounting (creating the DRAFT quote moves it `estimate` → `sign`). Freezing it
   * made "Continue to sign" silently do nothing. Still no client-side mutation: only
   * a fresher answer from `GET /bff/funnel` can move it, and only forwards. See the
   * note at the `useState` below and `furthestServerStep`.
   *
   * `undefined` means the rehydrate did not settle with a snapshot; the gate
   * treats that as no evidence either way and does not redirect.
   */
  readonly serverStep: FunnelStep | undefined;
  /**
   * Whether the step-one lead contact (E-CRM02) is on the session — seeded from
   * the rehydrate snapshot's `identity`, advanced by `markLeadContactCaptured`
   * when the contact panel submits. The landing page keeps the contact form in
   * front until this is true; a resolved meter alone never skips it.
   */
  readonly leadContactCaptured: boolean;
  readonly method: FunnelMethod;
  /** The opaque session-scoped handle the BFF minted (the redemption handle; AC3). */
  readonly candidateId: string | undefined;
  /**
   * The resolved meter the summary card + details dialog render. `undefined`
   * before the shopper has resolved a meter.
   */
  readonly meterDetail: MeterDetail | undefined;
  /** The 5-digit ZIP in play — the ZIP the shopper typed, or the resolved meter's ZIP. */
  readonly zip: string | undefined;
  /**
   * The chosen MONTHLY usage (kWh) the meter-filtered shelf (SHOP-5A) prices plans
   * at. `undefined` until the consumption section resolves a valid value; a Custom
   * entry that is blank/invalid also leaves it `undefined` (so the CTA stays off).
   */
  readonly monthlyUsageKwh: number | undefined;
  /** Which consumption choice produced `monthlyUsageKwh` ('low'|'medium'|'high'|'custom'). */
  readonly usageTier: ConsumptionSelectionId | undefined;
  /**
   * E-CRM04 — this session was rebuilt from a "resume your enrollment" link, so
   * the storefront greets the lead with a welcome-back banner. Read ONCE from the
   * seed (like every browser-state field) and never settable: it is the server's
   * fact about how the session was minted, and it grants nothing.
   */
  readonly resumed: boolean;
  /**
   * E-CRM04 — what the resume link restored (`full` / `progress`), or
   * undefined when not resumed. Read once, like `resumed`; grants nothing.
   */
  readonly resumeScope: 'full' | 'progress' | undefined;
}

/**
 * Whether this provider was mounted from a SETTLED rehydrate read — i.e. whether
 * the server has already been asked what this session holds.
 *
 * ── WHY A CONSUMER WOULD CARE ──────────────────────────────────────────────
 * "No candidate" means two completely different things: "we have not asked the
 * server yet" and "we asked, and there genuinely is nothing". A funnel step that
 * redirects on the first would bounce a returning shopper out of a funnel they were
 * legitimately in. In the storefront this is always `true`, because
 * `FunnelHydrationGate` does not render children until the query settles — the flag
 * exists so that stays checkable rather than assumed, and so a direct mount (a test,
 * a future embed) reads `false` honestly instead of claiming a hydration that never
 * happened.
 */
export interface FunnelHydration {
  readonly hydrated: boolean;
}

/** Setters — the whole API surface for mutating the funnel store. */
export interface FunnelActions {
  setMethod(method: FunnelMethod): void;
  markLeadContactCaptured(): void;
  setZip(zip: string | undefined): void;
  /**
   * Store a freshly-resolved meter + its opaque handle. The ZIP is taken from
   * the resolved meter so the card always renders a consistent ZIP/address pair,
   * regardless of on-ramp (the ESI ID path never typed a ZIP).
   */
  setResolved(candidateId: string, meter: MeterDetail): void;
  /**
   * Store the chosen monthly usage + which choice produced it. Pass `undefined`
   * for both to clear (an invalid Custom entry) — the shelf CTA reads this.
   */
  setConsumption(monthlyKwh: number | undefined, tier: ConsumptionSelectionId | undefined): void;
  /**
   * Drop the resolved meter so the shopper can start over (e.g. picks the
   * other tab, wants a different ZIP). Method + ZIP entry stay so we do not
   * yank inputs out from under the user; the meter card AND the consumption
   * choice clear (a new meter re-derives its own usage).
   */
  clearResolved(): void;
}

export type FunnelContextValue = FunnelState & FunnelActions & FunnelHydration;

const FunnelContext = createContext<FunnelContextValue | null>(null);

export interface FunnelProviderProps {
  readonly children: ReactNode;
  /**
   * Optional seed for TESTS / storybook. Takes precedence over `seed` — an explicit
   * fixture must never be silently overridden by whatever the environment rehydrated.
   */
  readonly initial?: Partial<FunnelState>;
  /**
   * The REHYDRATED seed: what `GET /bff/funnel` reported plus what the URL carried,
   * assembled by `buildFunnelSeed` and passed in by `FunnelHydrationGate`.
   *
   * ⚠️ KEPT DISTINCT FROM `initial` RATHER THAN REUSING IT. They are different
   * things with different lifetimes of trust: `initial` is a test fixture that must
   * win, `seed` is production data derived from the server. Collapsing them into one
   * prop would make "which one is this?" unanswerable at the call site, and would let
   * a test's fixture and a real hydration silently fight.
   *
   * It is read ONCE, as the initial value of each `useState` — a later snapshot
   * refetch deliberately does NOT clobber state the shopper has since changed. That
   * is why hydration happens in a GATE (which does not render children until the
   * read settles) rather than in an effect that fires after the funnel is already
   * live and interactive.
   */
  readonly seed?: FunnelSeed;
  /** True when `seed` came from a settled rehydrate read. See `FunnelHydration`. */
  readonly hydrated?: boolean;
}

export function FunnelProvider({
  children,
  initial,
  seed,
  hydrated = false,
}: FunnelProviderProps): JSX.Element {
  // ── ⚠️ THE ONE SEEDED VALUE THAT IS *NOT* READ-ONCE, AND WHY ──────────────
  //
  // Every other field here is browser state the shopper edits, and re-reading those
  // from a later snapshot would overwrite what they just typed. `serverStep` is not
  // that: it is the SERVER's own answer about what its session contains, and the
  // shopper advances it WITHOUT this page unmounting — pressing "Continue to sign"
  // creates the DRAFT quote, which moves the server from `estimate` to `sign`.
  //
  // Freezing it therefore broke the funnel: measured 2026-08-26, the draft quote
  // succeeded, `GET /bff/funnel` reported `step: "sign"`, `EstimatePage` called
  // `navigate(...sign)` — and `FunnelStepGate` bounced it back to `/estimate` off
  // this stale value. The button silently did nothing; only a hard reload got the
  // customer to the sign step.
  //
  // The fix keeps the no-setter rule intact — no component can advance this, only a
  // fresher server answer can — and takes the FURTHEST of the two. That is safe in
  // exactly one direction: `funnelGateFor` is forward-only, so a higher step can
  // only remove a redirect, and a lower one is ignored so a flaky read cannot
  // revoke proven progress. See `furthestServerStep`.
  const [seededServerStep] = useState<FunnelStep | undefined>(
    initial?.serverStep ?? seed?.serverStep,
  );
  const serverStep = furthestServerStep(seededServerStep, seed?.serverStep);
  const [method, setMethodState] = useState<FunnelMethod>(
    initial?.method ?? DEFAULT_FUNNEL_METHOD,
  );
  const [candidateId, setCandidateId] = useState<string | undefined>(
    initial?.candidateId ?? seed?.candidateId,
  );
  const [meterDetail, setMeterDetail] = useState<MeterDetail | undefined>(
    initial?.meterDetail ?? seed?.meterDetail,
  );
  const [zip, setZipState] = useState<string | undefined>(initial?.zip ?? seed?.zip);
  /* Product decision (2026-09-16): the details step fronts EVERY page load —
     the session's remembered contact no longer skips it (`seed.hasLeadContact`
     is deliberately not consulted). Re-submitting is harmless: the lead write
     is idempotent on the funnel session. */
  const [leadContactCaptured, setLeadContactCaptured] = useState<boolean>(
    initial?.leadContactCaptured ?? false,
  );
  const [monthlyUsageKwh, setMonthlyUsageKwh] = useState<number | undefined>(
    initial?.monthlyUsageKwh ?? seed?.monthlyUsageKwh,
  );
  const [usageTier, setUsageTier] = useState<ConsumptionSelectionId | undefined>(
    initial?.usageTier ?? seed?.usageTier,
  );
  const [resumed] = useState<boolean>(initial?.resumed ?? seed?.resumed ?? false);
  const [resumeScope] = useState<'full' | 'progress' | undefined>(
    initial?.resumeScope ?? seed?.resumeScope,
  );

  const setMethod = useCallback((next: FunnelMethod): void => {
    setMethodState(next);
  }, []);

  const setZip = useCallback((next: string | undefined): void => {
    setZipState(next);
  }, []);

  const setResolved = useCallback((handle: string, meter: MeterDetail): void => {
    setCandidateId(handle);
    setMeterDetail(meter);
    setZipState(meter.zip);
  }, []);

  const setConsumption = useCallback(
    (monthlyKwh: number | undefined, tier: ConsumptionSelectionId | undefined): void => {
      setMonthlyUsageKwh(monthlyKwh);
      setUsageTier(tier);
    },
    [],
  );

  const markLeadContactCaptured = useCallback((): void => {
    setLeadContactCaptured(true);
  }, []);

  const clearResolved = useCallback((): void => {
    setCandidateId(undefined);
    setMeterDetail(undefined);
    setMonthlyUsageKwh(undefined);
    setUsageTier(undefined);
  }, []);

  const value = useMemo<FunnelContextValue>(
    () => ({
      serverStep,
      method,
      candidateId,
      meterDetail,
      zip,
      monthlyUsageKwh,
      usageTier,
      resumed,
      resumeScope,
      hydrated,
      leadContactCaptured,
      setMethod,
      setZip,
      setResolved,
      setConsumption,
      clearResolved,
      markLeadContactCaptured,
    }),
    [
      serverStep,
      method,
      candidateId,
      meterDetail,
      zip,
      monthlyUsageKwh,
      usageTier,
      resumed,
      resumeScope,
      hydrated,
      leadContactCaptured,
      setMethod,
      setZip,
      setResolved,
      setConsumption,
      clearResolved,
      markLeadContactCaptured,
    ],
  );

  return <FunnelContext.Provider value={value}>{children}</FunnelContext.Provider>;
}

/**
 * Read the funnel store. Throws if used outside `<FunnelProvider>` because a
 * missing provider is a routing bug — silently defaulting would hide the
 * fact that a page rendered outside the storefront layout.
 */
export function useFunnel(): FunnelContextValue {
  const ctx = useContext(FunnelContext);
  if (ctx === null) {
    throw new Error('useFunnel must be used inside <FunnelProvider>.');
  }
  return ctx;
}

/**
 * Read the funnel store when one is mounted, or `null`.
 *
 * For layout chrome only (the E-CRM04 welcome-back banner): the storefront layout
 * is also rendered on its own, where "no funnel" is a legitimate state rather than
 * a routing bug. Pages keep using `useFunnel`, which throws.
 */
export function useOptionalFunnel(): FunnelContextValue | null {
  return useContext(FunnelContext);
}
